import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Crown, Zap } from "lucide-react";
import { usePlanUsage } from "@/hooks/useSubscription";

/**
 * "Active Plan · Remaining Posts: N" banner — mirror of the legacy widget.
 * Auto-hides when the plan-usage endpoint 401s (anon) or the query errors.
 */
export default function PlanUsageBanner({ className = "" }) {
  const { data, isLoading, isError } = usePlanUsage();

  if (isLoading) {
    return <div className={`skeleton h-12 rounded-xl ${className}`} />;
  }
  if (isError || !data) return null;

  const unlimited = data.unlimited;
  const remaining = data.remaining_posts;
  const low = !unlimited && typeof remaining === "number" && remaining <= 3;
  const zero = !unlimited && remaining === 0;

  return (
    <div
      data-testid="plan-usage-banner"
      className={`rounded-xl px-4 py-3 flex items-center justify-between gap-3 border ${
        zero
          ? "bg-red-50 border-red-200 text-red-800"
          : low
          ? "bg-amber-50 border-amber-200 text-amber-800"
          : "bg-emerald-50 border-emerald-200 text-emerald-800"
      } ${className}`}
    >
      <div className="flex items-center gap-2 text-sm font-semibold">
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        <span data-testid="plan-usage-name">Active Plan · {data.plan_name || "Free"}</span>
        <span className="opacity-60">·</span>
        <span data-testid="plan-usage-remaining">
          Remaining Posts: {unlimited ? "Unlimited" : remaining}
        </span>
        {typeof remaining === "number" && !unlimited && data.total_posts && (
          <span className="text-xs opacity-70">({data.active_posts}/{data.total_posts} used)</span>
        )}
      </div>
      {(low || zero) && (
        <Link
          to={data.upgrade_url || "/plan-and-pricing"}
          data-testid="plan-usage-upgrade"
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-xs font-bold px-3 py-1.5 transition-colors whitespace-nowrap"
        >
          <Crown className="w-3.5 h-3.5" /> Upgrade
        </Link>
      )}
    </div>
  );
}
